import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import Box from 'ui-box';

import PreImportForm from './PreImportForm';
import ImportCSVFile from './ImportCSVFile';
import PreviewImport from './Preview';

import { ISideSheetProps, SideSheet } from '../../../npaper';
import { ICompany, useStores } from '../../../stores';
import { animated, useSprings } from 'react-spring';

export interface IImportProductsSideSheetProps
  extends Omit<ISideSheetProps, 'title' | 'actions'> {}

type ImportDataItemType = {
  sku: string;
  name?: string;
  description?: string;
  product?: number | string;
  retail_price?: number;
  manufacturer_number?: string;
  cost_price?: number;
  stock?: number;
};

type ImportDataType = {
  currency: string;
  supplier?: number;
  items: ImportDataItemType[];
};

export const ImportProductsSideSheet: React.FC<IImportProductsSideSheetProps> =
  ({ ...sideSheetProps }) => {
    const intl = useIntl();

    const [step, setStep] = useState(0);
    const springs = useSprings(
      4,
      new Array(4).fill(0).map((_, i) => ({
        opacity: i === step ? 1 : 0,
        display: i === step ? 'block' : 'none',
        transform: i === step ? 'translateY(0)' : 'translateY(-32px)',
      }))
    );

    useEffect(() => {
      if (!sideSheetProps.isShown) {
        setStep(0);
      }
    }, [sideSheetProps.isShown]);

    const { authStore } = useStores();
    const [supplier, setSupplier] = useState<ICompany>();
    const [currency, setCurrency] = useState<string>(
      authStore.currentOrganisation!.default_currency
    );

    const [importData, setImportData] = useState<ImportDataType>();

    const startOver = () => {
      setImportData(undefined);
      setSupplier(undefined);
      setCurrency(authStore.currentOrganisation!.default_currency);
    };

    return (
      <SideSheet
        {...sideSheetProps}
        title={intl.formatMessage({
          defaultMessage: 'Import products',
        })}
        onClose={() => {
          startOver();
          sideSheetProps.onClose();
        }}
      >
        <Box width="100%">
          <animated.div style={springs[0]}>
            <PreImportForm
              onContinue={(supplier, currency) => {
                setSupplier(supplier);
                setCurrency(currency);
                setStep(1);
              }}
            />
          </animated.div>
          <animated.div style={springs[1]}>
            <ImportCSVFile
              onBack={() => {
                setStep(0);
              }}
              onContinue={(data) => {
                setStep(2);
                setImportData(data);
              }}
              currency={currency}
              supplier={supplier}
            />
          </animated.div>
          <animated.div style={springs[2]}>
            <PreviewImport
              currency={currency}
              supplier={supplier}
              importData={importData}
              onBack={() => {
                setStep(1);
              }}
              onSuccess={() => {
                startOver();
                sideSheetProps.onClose();
              }}
            />
          </animated.div>
        </Box>
      </SideSheet>
    );
  };
